// @flow
import mapValues from 'lodash/fp/mapValues';
import groupBy from 'lodash/fp/groupBy';
import pipe from 'lodash/fp/pipe';
import fromPairs from 'lodash/fp/fromPairs';
import flatMap from 'lodash/fp/flatMap';
import map from 'lodash/fp/map';

import * as at from '../actions/action-types';
import {
  productNumberGivenStockItemKey,
  variationStockItems,
  variationGivenCode,
} from '../modules/product';
import { externalPricesKey, parsedErrors } from '../modules/external-prices';
// types
import type { Action } from '../store';

export type ProductSlice = $ReadOnly<{
  externalErrors?: $ReadOnlyArray<string>,
  isLoading?: boolean,
  stockItems: {
    +[stockItemKey: string]: $ReadOnly<{
      value: ?number,
      error: ?string,
    }>,
  },
}>;

export type State = $ReadOnly<{
  [externalPricesKey: string]: $ReadOnly<{
    // maybe have a timestamp or something in here
    // timestamp: xxxx,
    products: {
      +[productNumber: string]: ProductSlice,
    },
  }>,
}>;

const EXTERNAL_PRICES = 'EXTERNAL_PRICES';

export const initialState: State = { ...null };

export default (state: State = initialState, action: Action): State => {
  if (
    action.payload &&
    action.payload.resourceName === EXTERNAL_PRICES &&
    action.payload.resourceId
  ) {
    switch (action.type) {
      case at.RESOURCE_REQUEST:
        if (action.payload.method === 'GET') {
          // I'm fine with this flow hack since we are planning on changing the api soon, right?
          const { orderType, catalogKey, customerNumber, products } = (action
            .payload.resourceId: any);

          const key = externalPricesKey(orderType, catalogKey, customerNumber);

          const obj = fromPairs(
            products.map((product) => [
              product.number,
              {
                isLoading: true,
                stockItems: { ...null },
              },
            ]),
          );
          return {
            ...state,
            [key]: {
              products: {
                ...(state[key] ? state[key].products : { ...null }),
                ...obj,
              },
            },
          };
        }
        return state;

      case at.RESOURCE_FAIL_TO_RECEIVE:
        if (action.payload.method === 'GET') {
          // I'm fine with this flow hack since we are planning on changing the api soon, right?
          const { orderType, catalogKey, customerNumber, products } = (action
            .payload.resourceId: any);

          const key = externalPricesKey(orderType, catalogKey, customerNumber);

          const obj = fromPairs(
            products.map((product) => [
              product.number,
              {
                ...state[product.number],
                isLoading: false,
                error: 'api_call_failed',
              },
            ]),
          );
          return {
            ...state,
            [key]: {
              products: {
                ...state[key].products,
                ...obj,
              },
            },
          };
        }
        return state;

      case at.RESOURCE_RECEIVE:
        if (action.payload.method === 'GET') {
          // I'm fine with this flow hack since we are planning on changing the api soon, right?
          const { orderType, catalogKey, customerNumber, products } = (action
            .payload.resourceId: any);

          const key = externalPricesKey(orderType, catalogKey, customerNumber);

          const errors: $ReadOnlyArray<{
            productNumber?: string,
            variationCode?: string,
            clientMessage: string,
          }> = parsedErrors(action.payload.data.errors);

          const erroredStockItemKeys = flatMap(
            ({ productNumber, variationCode }): $ReadOnlyArray<string> => {
              const product = products.find(
                ({ number }) => productNumber === number,
              );
              const variation = variationGivenCode(
                variationCode || '',
                product,
              );
              const stockItemKeys = variation
                ? variationStockItems(variation).map((si) => si.key)
                : [];
              return stockItemKeys;
            },
            errors,
          );

          const stockItemPricesGroupedByProduct = pipe(
            Object.entries,
            groupBy(([stockItemKey, _price]) =>
              productNumberGivenStockItemKey(products, stockItemKey),
            ),
            /* ⚠️ this nested mapValues is hard to grok */
            mapValues((arr) => ({
              stockItems: pipe(
                map(([siKey, value]) => [
                  siKey,
                  {
                    value,
                    error: erroredStockItemKeys.includes(siKey)
                      ? 'client_system_error'
                      : null,
                  },
                ]),
                fromPairs,
              )(arr),
              //   mapValues(value => ({
              //   value,
              //   error: erroredStockItemKeys.includes(arr[0]) ? 'client_system_error' : null,
              //   // arr is [[asdf, 1], ...]
              // }))(fromPairs(arr) || {}),
              isLoading: false,
            })),
          )(action.payload.data.data);

          const obj = {
            /* 💁 We still need to change the loading state for all products in the query
             in case the response returned no stock item prices for a particular product. */
            ...fromPairs(
              products.map((product) => [
                product.number,
                {
                  ...state[product.number],
                  isLoading: false,
                },
              ]),
            ),
            ...stockItemPricesGroupedByProduct,
          };

          const withErrors = fromPairs(
            Object.keys(obj)
              .map((k) => [k, obj[k]])
              .map(([number, productEntry]) => [
                number,
                {
                  ...productEntry,
                  externalErrors: errors
                    .filter(({ productNumber }) => productNumber === number)
                    .map(({ clientMessage }) => clientMessage),
                },
              ]),
          );

          return {
            ...state,
            [key]: {
              products: {
                ...state[key].products,
                ...withErrors,
              },
            },
          };
        }
        return state;

      case at.EXTERNAL_PRICES_CLEAR:
        return initialState;

      default:
        return state;
    }
  }
  return state;
};
